import * as React from "react";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import TranslateIcon from "@mui/icons-material/Translate";

/**
 * localStorage key read by `src/i18n/index.ts` on startup to restore the
 * previously selected language. This component is the only writer.
 */
export const LANGUAGE_STORAGE_KEY = "ukar_language";

/** Supported locales — must stay in sync with the resources in `src/i18n/index.ts`. */
export const SUPPORTED_LANGUAGES = [
  { code: "en", labelKey: "language.en" },
  { code: "ru", labelKey: "language.ru" },
  { code: "az", labelKey: "language.az" },
  { code: "ge", labelKey: "language.ge" },
] as const;

export default function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    // Persist so `src/i18n/index.ts` restores this choice on next page load.
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    handleClose();
  };

  // i18n.language can carry a region suffix (e.g. "en-US"); compare on the base tag.
  const activeCode = (i18n.language || "en").split("-")[0];

  return (
    <>
      <Tooltip title={t("language.select")}>
        <IconButton
          onClick={handleOpen}
          color="inherit"
          aria-label={t("language.select")}
          aria-haspopup="true"
          sx={{ mr: 1 }}
        >
          <TranslateIcon />
        </IconButton>
      </Tooltip>

      <Menu
        sx={{ mt: "45px" }}
        id="menu-language"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {SUPPORTED_LANGUAGES.map((language) => (
          <MenuItem
            key={language.code}
            selected={language.code === activeCode}
            onClick={() => handleSelect(language.code)}
          >
            <ListItemText>
              <Typography sx={{ textAlign: "left" }}>
                {t(language.labelKey)}
              </Typography>
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
